import * as XLSX from "xlsx-js-style"
import {ExportExcelPayload} from "./export-table"

export type ExportXLSXPayload = {
    filename: string
    sheets: XLSXSheet[]
}

export type XLSXMergeRange = {
    s: {r: number; c: number}
    e: {r: number; c: number}
}

export type XLSXSheet = {
    name?: string
    heading?: XLSXCell[][]
    colsWidth?: number[]
    data: any[]
    /** When true, `data` is `string[][]` and is written with `sheet_add_aoa` (e.g. grouped headers + flat rows). */
    dataAsAoa?: boolean
    merges?: XLSXMergeRange[]
}

export type XLSXCell = {
    v: string | number // cell value
    t?: string // cell data type
    s?: any // cell style
}

const applyWrapTextToMultilineCells = (worksheet: XLSX.WorkSheet, startRow = 0) => {
    const range = worksheet["!ref"] ? XLSX.utils.decode_range(worksheet["!ref"]) : null
    if (!range) return

    for (let row = Math.max(range.s.r, startRow); row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellRef = XLSX.utils.encode_cell({r: row, c: col})
            const cell = worksheet[cellRef]
            if (!cell || typeof cell.v !== "string" || !/[\n\r]/.test(cell.v)) {
                continue
            }

            cell.s = {
                ...(cell.s || {}),
                alignment: {
                    ...(cell.s?.alignment || {}),
                    wrapText: true,
                    vertical: "top"
                }
            }
        }
    }
}

export const exportExcel = ({columns, rows, worksheet = "Worksheet", filename}: ExportExcelPayload) => {
    const style = {font: {bold: true}}
    const sheetHeader: XLSXCell[] = columns.map((x) => {
        return {
            v: x,
            s: style
        }
    })

    const heading: XLSXCell[][] = [sheetHeader]
    const sheets: XLSXSheet[] = [
        {
            name: worksheet,
            heading,
            data: rows
        }
    ]

    exportXlsx({
        filename,
        sheets
    })
}

export const exportXlsx = (payload: ExportXLSXPayload) => {
    const {filename, sheets} = payload
    const workbook = XLSX.utils.book_new()

    sheets.forEach((x, index) => {
        const worksheet = XLSX.utils.json_to_sheet([])
        if (x.heading) {
            XLSX.utils.sheet_add_aoa(worksheet, x.heading)
        }
        if (x.colsWidth) {
            worksheet["!cols"] = x.colsWidth.map((y) => {
                return {
                    width: y
                }
            })
        }

        const bodyOriginRow = (x.heading?.length || 0) + 1
        if (x.dataAsAoa) {
            XLSX.utils.sheet_add_aoa(worksheet, x.data, {origin: `A${bodyOriginRow}`})
        } else {
            XLSX.utils.sheet_add_json(worksheet, x.data, {
                origin: `A${bodyOriginRow}`,
                skipHeader: !!x.heading
            })
        }
        if (x.merges?.length) {
            worksheet["!merges"] = x.merges
        }
        applyWrapTextToMultilineCells(worksheet)
        XLSX.utils.book_append_sheet(workbook, worksheet, x.name || `Sheet${index + 1}`)
    })

    XLSX.writeFile(workbook, `${filename}.xlsx`)
}
